import { Injectable, Signal, computed, signal } from '@angular/core';
import { PaymentApiError, PaymentAuthError } from '@4players/payment';
import type {
  UsageCostMetric,
  UsageCostProduct,
  UsageCostSubtype,
  UsageCostSummary,
} from '@4players/payment';
import { firstValueFrom } from 'rxjs';
import { App, hasActivatedProduct } from 'app/core/app/app.model';
import { AppService } from 'app/core/app/app.service';
import {
  aggregateFleetResourceWallets,
  FleetResourceWallet,
} from 'app/core/fleet/fleet-wallet';
import { FleetWalletService } from 'app/core/fleet/fleet-wallet.service';
import { PaymentService } from 'app/core/payment/payment.service';
import {
  getProductDisplay,
  looksLikeTranslationKey,
} from 'app/core/payment/product-display';
import { PLAN_INCLUDED_LIMITS } from './consumption-limits';
import {
  ConsumptionMeter,
  ConsumptionMode,
  ConsumptionSnapshot,
  KpiId,
} from './consumption.model';

/** How long a computed snapshot stays fresh before a reload refetches. */
const CACHE_TTL_MS = 60_000;

/** A snapshot cached per project + calendar month. */
interface CacheEntry {
  at: number;
  snapshot: ConsumptionSnapshot;
}

interface FleetWalletLoad {
  resources: FleetResourceWallet[] | null;
  failed: boolean;
}

type PlanLimits = Partial<Record<KpiId, number>>;

/**
 * Fleet resource definitions shared by wallet-backed quota meters and the
 * Payment allocation metrics that remain hidden on pay-as-you-go.
 */
const FLEET_ALLOCATION_METRICS = [
  {
    kind: 'cpu' as const,
    key: 'cpu-hours',
    limitKey: 'fleet.cpu-hours' as KpiId,
    label: 'CPU Hours',
    unit: 'CPUh',
    icon: 'heroicons_outline:cpu-chip',
  },
  {
    kind: 'memory' as const,
    key: 'memory-gb-hours',
    limitKey: 'fleet.memory-gb-hours' as KpiId,
    label: 'Memory GB Hours',
    unit: 'GBh',
    icon: 'heroicons_outline:circle-stack',
  },
];

/**
 * Provides the unified month-to-date consumption picture for the active
 * project across all products (Voice, Rooms, Fleet, Cortex).
 *
 * Payment's aggregated usage-cost summary supplies every product's monthly
 * usage and costs. Fleet quota plans are the deliberate exception: the Fleet
 * wallets are authoritative because their balances are what Fleet enforces.
 * The service reacts to `AppService.activeApp$` changes and exposes the same
 * snapshot to the header meter and the "Usage & Costs" settings panel.
 *
 * Semantics by billing state:
 * - active (pay-as-you-go) → `payg`: usage × unit price = cost, straight
 *   from the summary. Unpriced metrics show the 0 € placeholder. Fleet's
 *   allocation metrics (cpu-hours / memory-gb-hours) are hidden — billing
 *   happens per instance-day.
 * - external → no meters (billing handled outside the console).
 * - free / mini / indie → `quota`: non-Fleet metrics from Payment are joined
 *   with hardcoded plan limits. Fleet CPU and memory remain separate and use
 *   `initial - balance` from their respective wallet families.
 * - other non-PAYG states retain the quota presentation without inventing a
 *   Fleet allowance for a plan whose wallet semantics are unknown.
 *
 * Voice hosting: the summary delivers the metrics that apply to the
 * project's `hostingType` — PCUs for cloud-hosted projects, ODIN server
 * instances for on-premise ones. On-premise metrics don't match the PCU
 * plan limit, so they automatically render as usage-only rows.
 */
@Injectable({ providedIn: 'root' })
export class ConsumptionService {
  private _snapshot = signal<ConsumptionSnapshot | null>(null);
  private _loading = signal<boolean>(false);

  /** The latest consumption snapshot for the active project (null = none). */
  readonly snapshot: Signal<ConsumptionSnapshot | null> =
    this._snapshot.asReadonly();

  /** Whether usage data is currently being (re)fetched. */
  readonly loading: Signal<boolean> = this._loading.asReadonly();

  /** Presentation mode derived from the active project's billing state. */
  readonly mode: Signal<ConsumptionMode> = computed(
    () => this._snapshot()?.mode ?? 'none',
  );

  /** Highest consumed fraction across all limited meters (null = none). */
  readonly worstPercent: Signal<number | null> = computed(() => {
    const meters = this._snapshot()?.meters ?? [];
    const percents = meters
      .map((m) => m.consumedPercent)
      .filter((p): p is number => p !== null);
    return percents.length > 0 ? Math.max(...percents) : null;
  });

  /** Month-to-date cost in cents (PAYG mode only, else null). */
  readonly totalCostCents: Signal<number | null> = computed(() => {
    const snap = this._snapshot();
    if (!snap || snap.mode !== 'payg') {
      return null;
    }
    return (
      snap.totalCostCents ??
      snap.meters.reduce((sum, m) => sum + (m.costCents ?? 0), 0)
    );
  });

  /** Display currency of the summary (default EUR). */
  readonly currency: Signal<string> = computed(
    () => this._snapshot()?.currency ?? 'EUR',
  );

  /** Monotonic token used to drop stale responses on rapid project switches. */
  private _runToken = 0;

  /** Snapshot cache keyed by project + calendar month + mode. */
  private _cache = new Map<string, CacheEntry>();

  constructor(
    private _appService: AppService,
    private _paymentService: PaymentService,
    private _fleetWalletService: FleetWalletService,
  ) {
    // Root-scoped service: lives for the app lifetime, no teardown needed.
    this._appService.activeApp$.subscribe((app) => {
      void this._load(app, false);
    });
  }

  /**
   * Recompute the snapshot for the current active project.
   *
   * @param force - Bypass the TTL cache (used by the panel's refresh button).
   */
  refresh(force = false): void {
    void this._load(this._appService.activeApp, force);
  }

  // ------------------------------------------------------------------
  // Snapshot assembly
  // ------------------------------------------------------------------

  private async _load(app: App | null, force: boolean): Promise<void> {
    const token = ++this._runToken;

    if (!app?.id) {
      this._snapshot.set(null);
      this._loading.set(false);
      return;
    }

    const mode = this._resolveMode(app.billingState);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const cacheKey = `${app.id}:${now.getFullYear()}-${now.getMonth()}:${mode}`;

    // Proactive permission gate: the project response carries a
    // `costControl` permission — when it is explicitly false the user may
    // not see usage & cost data, so skip all fetches and surface the
    // permission-denied state right away (checked before the cache so a
    // permission change is always respected).
    if (
      (mode === 'quota' || mode === 'payg') &&
      app.permissions?.costControl === false
    ) {
      this._snapshot.set({
        ...this._emptySnapshot(mode, app.id, monthStart, now),
        permissionDenied: true,
      });
      this._loading.set(false);
      return;
    }

    if (!force) {
      const cached = this._cache.get(cacheKey);
      if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
        this._snapshot.set(cached.snapshot);
        this._loading.set(false);
        return;
      }
    }

    if (mode === 'external' || mode === 'none') {
      const snapshot = this._emptySnapshot(mode, app.id, monthStart, now);
      this._cache.set(cacheKey, { at: Date.now(), snapshot });
      this._snapshot.set(snapshot);
      this._loading.set(false);
      return;
    }

    this._loading.set(true);

    const isPayg = mode === 'payg';
    // Unknown quota-style billing states (future tiers) simply have no
    // entry here — their metrics render as usage-only rows.
    const limits: PlanLimits | null = isPayg
      ? null
      : (PLAN_INCLUDED_LIMITS[app.billingState as 'free' | 'mini' | 'indie'] ??
        null);

    const fleetAppId = this._fleetAppId(app.appId);
    const hasFleet = hasActivatedProduct(app, 'fleet') || fleetAppId !== null;
    const usesFleetWallets =
      app.billingState === 'free' ||
      app.billingState === 'mini' ||
      app.billingState === 'indie';
    const fleetWalletPromise: Promise<FleetWalletLoad> =
      usesFleetWallets && hasFleet
        ? fleetAppId === null
          ? Promise.resolve({ resources: null, failed: true })
          : firstValueFrom(
              this._fleetWalletService.getAppWallets(fleetAppId, force),
            ).then(
              (wallets) => ({
                resources: aggregateFleetResourceWallets(wallets),
                failed: false,
              }),
              () => ({ resources: null, failed: true }),
            )
        : Promise.resolve({ resources: null, failed: false });

    // The one-call usage & cost summary (defaults to current month-to-date).
    // The endpoint is restricted to the project owner; for team members it
    // fails with an auth-shaped error that must NOT be confused with an
    // expired session (PaymentService already skips the sign-out for it).
    let summary: UsageCostSummary | null;
    let permissionDenied = false;
    try {
      summary = await this._paymentService.getUsageCostSummary(app.id);
    } catch (error) {
      summary = null;
      permissionDenied = this._isPermissionError(error);
    }
    const fleetWalletLoad = await fleetWalletPromise;
    if (token !== this._runToken) {
      return;
    }

    const meters: ConsumptionMeter[] = [];
    const activatedProductSlugs: string[] = [];
    const inactiveProductSlugs: string[] = [];

    for (const product of summary?.products ?? []) {
      if (!product.activated) {
        if (product.slug) {
          inactiveProductSlugs.push(product.slug);
        }
        continue;
      }
      if (product.slug) {
        activatedProductSlugs.push(product.slug);
      }
      // Fleet quota usage comes from the independently enforced CPU/RAM
      // wallets below, never from Payment's month-to-date allocation totals.
      if (!isPayg && product.slug === 'fleet') {
        continue;
      }
      meters.push(...this._metersFromProduct(product, summary, limits, isPayg));
    }

    if (usesFleetWallets && hasFleet) {
      if (!activatedProductSlugs.includes('fleet')) {
        activatedProductSlugs.push('fleet');
      }
      const inactiveFleet = inactiveProductSlugs.indexOf('fleet');
      if (inactiveFleet >= 0) {
        inactiveProductSlugs.splice(inactiveFleet, 1);
      }
      if (fleetWalletLoad.resources) {
        meters.push(
          ...this._fleetQuotaMetersFromWallets(fleetWalletLoad.resources),
        );
      }
    }

    const snapshot: ConsumptionSnapshot = {
      mode,
      projectId: app.id,
      periodStart: this._parsePeriodDate(summary?.periodStart, monthStart),
      periodEnd: this._parsePeriodDate(summary?.periodEnd, now),
      meters,
      activatedProductSlugs,
      inactiveProductSlugs,
      totalCostCents: isPayg ? (summary?.totalCost ?? null) : null,
      currency: (summary?.currency ?? 'EUR').toUpperCase(),
      loadError: summary === null && !permissionDenied,
      permissionDenied,
      productErrors: fleetWalletLoad.failed
        ? { fleet: 'Fleet wallets could not be loaded.' }
        : {},
    };
    this._cache.set(cacheKey, { at: Date.now(), snapshot });
    this._snapshot.set(snapshot);
    this._loading.set(false);
  }

  private _emptySnapshot(
    mode: ConsumptionMode,
    projectId: string,
    periodStart: Date,
    periodEnd: Date,
  ): ConsumptionSnapshot {
    return {
      mode,
      projectId,
      periodStart,
      periodEnd,
      meters: [],
      activatedProductSlugs: [],
      inactiveProductSlugs: [],
      totalCostCents: null,
      currency: 'EUR',
      loadError: false,
      permissionDenied: false,
      productErrors: {},
    };
  }

  /**
   * Whether an error from the owner-restricted usage-cost endpoint means
   * "no permission" (401/403 or app-error code 1000) rather than a
   * transient failure.
   */
  private _isPermissionError(error: unknown): boolean {
    if (error instanceof PaymentAuthError) {
      return true;
    }
    if (error instanceof PaymentApiError) {
      return (
        error.httpStatus === 401 ||
        error.httpStatus === 403 ||
        error.code === 1000
      );
    }
    return false;
  }

  private _resolveMode(billingState: string | undefined): ConsumptionMode {
    switch (billingState) {
      case 'active':
        return 'payg';
      case 'external':
        return 'external';
      case undefined:
        return 'none';
      default:
        // Every non-PAYG, non-external state (free / mini / indie /
        // cancelled / future subscription tiers) gets the quota-style
        // view; unknown tiers just have no limits table entry.
        return 'quota';
    }
  }

  // ------------------------------------------------------------------
  // Summary → meters
  // ------------------------------------------------------------------

  private _metersFromProduct(
    product: UsageCostProduct,
    summary: UsageCostSummary | null,
    limits: PlanLimits | null,
    isPayg: boolean,
  ): ConsumptionMeter[] {
    const meters: ConsumptionMeter[] = [];
    const subtypes = product.subtypes ?? [];
    subtypes.forEach((subtype, subtypeIndex) => {
      for (const metric of subtype.metrics ?? []) {
        // Fleet's allocation metrics are informational on PAYG (billing is
        // per instance-day) — only subscription plans display them.
        if (
          isPayg &&
          product.slug === 'fleet' &&
          FLEET_ALLOCATION_METRICS.some((def) => def.key === metric.key)
        ) {
          continue;
        }
        meters.push(
          this._meterFromMetric(
            product,
            subtype,
            subtypeIndex,
            metric,
            summary,
            limits,
            isPayg,
          ),
        );
      }
    });
    return meters;
  }

  private _meterFromMetric(
    product: UsageCostProduct,
    subtype: UsageCostSubtype,
    subtypeIndex: number,
    metric: UsageCostMetric,
    summary: UsageCostSummary | null,
    limits: PlanLimits | null,
    isPayg: boolean,
  ): ConsumptionMeter {
    const slug = product.slug ?? 'unknown';
    const key = metric.key ?? 'metric';
    const quantity = metric.quantity ?? 0;

    // Plan limits are keyed `<slug>.<metricKey>` (e.g. "voice.pcu"). The
    // Cortex session allowance is a *concurrent* limit while the metric
    // counts sessions in the period — surface it as a footnote instead of
    // a percent bar. On-premise Voice metrics aren't PCU-keyed, so the PCU
    // limit never applies to them.
    let included: number | null = null;
    let infoNote: string | null = null;
    if (!isPayg && limits) {
      if (slug === 'cortex' && key.includes('session')) {
        const allowance = limits['cortex.sessions'];
        if (allowance !== undefined) {
          infoNote = `Plan allows ${allowance} concurrent session${allowance === 1 ? '' : 's'}`;
        }
      } else {
        included = limits[`${slug}.${key}` as KpiId] ?? null;
      }
    }
    const consumedPercent =
      included !== null && included > 0
        ? Math.max(0, Math.min(1, quantity / included))
        : null;

    const unitPriceCents = isPayg ? (metric.unitAmount ?? null) : null;
    // Unpriced PAYG metrics keep the required 0 € placeholder.
    const costCents = isPayg ? (metric.cost ?? 0) : null;

    return {
      productSlug: slug,
      kpiId: `${slug}.${subtype.slug ?? subtypeIndex}.${key}`,
      metricKey: key,
      label: metric.label ?? key,
      // Subtype names may arrive as unresolved translation keys
      // (e.g. "product.voice.name") — drop those instead of showing them.
      subtypeName: looksLikeTranslationKey(subtype.name)
        ? null
        : (subtype.name ?? null),
      unit: this._displayUnit(key, metric.unit),
      icon: this._metricIcon(slug, key),
      consumed: quantity,
      included,
      consumedPercent,
      unitPriceCents,
      priceUnit: metric.unit ?? null,
      costCents,
      currency: (metric.currency ?? summary?.currency ?? 'EUR').toUpperCase(),
      rechargeAt: null,
      infoNote,
      isEstimate: isPayg,
    };
  }

  /** Short display unit for a metric (falls back to the billing unit). */
  private _displayUnit(key: string, billingUnit: string | undefined): string {
    if (key === 'pcu') return 'PCU';
    if (key.includes('cpu')) return 'CPUh';
    if (key.includes('memory') || key.includes('ram')) return 'GBh';
    if (key.includes('instance')) return 'days';
    if (key.includes('minute')) return 'min';
    if (key.includes('session')) return 'sessions';
    if (key.includes('room')) return 'rooms';
    return billingUnit ?? '';
  }

  /** Icon for a metric, falling back to the product's icon. */
  private _metricIcon(slug: string, key: string): string {
    if (key === 'pcu') return 'heroicons_outline:microphone';
    if (key.includes('cpu')) return 'heroicons_outline:cpu-chip';
    if (key.includes('memory') || key.includes('ram')) {
      return 'heroicons_outline:circle-stack';
    }
    if (key.includes('instance')) return 'heroicons_outline:server-stack';
    if (key.includes('minute') || key.includes('transcription')) {
      return 'heroicons_outline:language';
    }
    if (key.includes('session')) {
      return 'heroicons_outline:chat-bubble-left-right';
    }
    if (key.includes('room')) return 'heroicons_outline:video-camera';
    return getProductDisplay(slug).icon;
  }

  // ------------------------------------------------------------------
  // Fleet on subscription plans
  // ------------------------------------------------------------------

  /**
   * Fleet meters for subscription plans. CPU and RAM are already folded
   * independently by `aggregateFleetResourceWallets`; this mapping only turns
   * those two authoritative balances into the common presentation model.
   */
  private _fleetQuotaMetersFromWallets(
    resources: FleetResourceWallet[],
  ): ConsumptionMeter[] {
    return resources.flatMap((resource) => {
      const def = FLEET_ALLOCATION_METRICS.find(
        (candidate) => candidate.kind === resource.kind,
      );
      return def
        ? [
            {
              productSlug: 'fleet',
              kpiId: def.limitKey,
              metricKey: def.key,
              label: def.label,
              subtypeName: null,
              unit: def.unit,
              icon: def.icon,
              consumed: resource.used,
              included: resource.initialBalance,
              consumedPercent: resource.usedPercent,
              unitPriceCents: null,
              priceUnit: null,
              costCents: null,
              currency: 'EUR',
              rechargeAt: resource.rechargeAt,
              infoNote: null,
              isEstimate: false,
            },
          ]
        : [];
    });
  }

  private _fleetAppId(value: string | undefined): number | null {
    const appId = Number(value);
    return Number.isInteger(appId) && appId > 0 ? appId : null;
  }

  /** Parse a summary "YYYY-MM-DD" period boundary, with a local fallback. */
  private _parsePeriodDate(value: string | undefined, fallback: Date): Date {
    if (!value) {
      return fallback;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
  }
}
